import type { ComponentProps, FunctionComponent, ReactElement } from 'react';
import { useMemo } from 'react';
import Select, {
  components,
  type GroupType,
  type OptionProps,
  type OptionType,
  type StylesConfig,
} from '@atlaskit/select';
import { AnyAri } from '@atlassian/ari';
import { defineMessages, useIntl } from 'react-intl';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  useInfo,
  useSelectedWorkspace,
} from '../../../Flow/ConsentContextProvider';
import SiteTile from '../../../Tile/SiteTile/SiteTile';
import {
  getAvatarIconColor,
  ACCENT,
  type SiteTileColor,
} from '../../../Tile/siteTileColors';
import {
  useRovoMostUsedSites,
  type RovoMostUsedSite,
} from '../domain/mostUsedSites';
import Label from '../Label';
import useRovoVisualRefresh from '../useRovoVisualRefresh';
import type { AvailableSite } from '../../../../definitions/consent-info';

const messages = defineMessages({
  siteLabel: {
    id: 'siteSelector.label',
    defaultMessage: 'Use app on',
    description: 'Description for which sites user wants to authorize',
  },
  sitePlaceholder: {
    id: 'siteSelector.sitePlaceholder',
    defaultMessage: 'Select',
    description: 'Placeholder for which sites user wants to authorize',
  },
  recentGroupLabel: {
    id: 'siteSelector.recentGroupLabel',
    defaultMessage: 'Recently used',
    description: 'Group label for sites the user has used recently',
  },
  allSitesGroupLabel: {
    id: 'siteSelector.allSitesGroupLabel',
    defaultMessage: 'All',
    description: 'Group label for all sites the user can authorize',
  },
  siteLabelCount: {
    id: 'siteSelector.refreshed.label',
    defaultMessage: '{count, plural, one {Site} other {Sites}}',
    description:
      'Field label shown above the refreshed site selector, pluralised based on the number of selected sites',
  },
});

const siteSelectStyles = xcss({
  minWidth: '250px',
});

const refreshedWrapperStyles = xcss({
  width: '100%',
  alignSelf: 'stretch',
});

const refreshedStackStyles = xcss({
  width: '100%',
});

const groupLabelStyles = xcss({
  paddingBlock: 'space.050',
  paddingInline: 'space.025',
});

interface ParsedWorkspace {
  name: string;
  url: string;
}

type WorkspaceOption = OptionType & {
  siteId: string | null;
  workspace: string;
  avatarUrl?: string;
};

type WorkspaceOptions = ReadonlyArray<
  WorkspaceOption | GroupType<WorkspaceOption>
>;

export interface SelectorProps {
  availableWorkspaces: string[];
  siteMapping?: Record<string, string>;
  availableSites?: AvailableSite[];
  selectedWorkspace: string | null;
  setSelectedWorkspace: (workspace: string | null) => void;
  mostUsedSites?: RovoMostUsedSite[];
  shouldGroupMostUsedSites?: boolean;
}

/**
 * Derive a human-friendly display name from a bare site URL by taking the first
 * DNS label and capitalising it, e.g. `mario.atlassian.net` -> `Mario`. Falls
 * back to the original string if there is no recognisable subdomain.
 */
const deriveNameFromUrl = (url: string): string => {
  const firstLabel = url.split('.')[0]?.trim();
  if (!firstLabel) {
    return url;
  }
  return firstLabel.charAt(0).toUpperCase() + firstLabel.slice(1);
};

/**
 * Workspaces may arrive as `name|url` (e.g. `Hello|hello.atlassian.net`) or as a
 * bare URL (e.g. `mario.atlassian.net`). When an explicit `name|url` is given,
 * split on the first `|`. Otherwise derive the display name from the URL's
 * subdomain so both the name and the URL can be shown.
 */
const parseWorkspace = (workspace: string): ParsedWorkspace => {
  const separatorIndex = workspace.indexOf('|');
  if (separatorIndex === -1) {
    return { name: deriveNameFromUrl(workspace), url: workspace };
  }
  return {
    name: workspace.slice(0, separatorIndex),
    url: workspace.slice(separatorIndex + 1),
  };
};

function getSiteIdFromSiteMappingKey(siteMappingKey: string): string {
  try {
    const ari = AnyAri.parse(siteMappingKey);

    if (ari.resourceType === 'site') {
      return ari.resourceId;
    }
  } catch {
    return siteMappingKey;
  }

  return siteMappingKey;
}

function getSiteIdsByWorkspace(siteMapping?: Record<string, string>) {
  const siteIdsByWorkspace = new Map<string, string>();

  for (const [siteMappingKey, workspace] of Object.entries(siteMapping ?? {})) {
    const siteId = getSiteIdFromSiteMappingKey(siteMappingKey);
    siteIdsByWorkspace.set(workspace, siteId);
  }

  return siteIdsByWorkspace;
}

/**
 * Unified site info lookup: prefers `availableSites` when present (provides
 * both siteId and avatar), otherwise falls back to `siteMapping` (siteId only).
 */
interface SiteInfo {
  siteId: string;
  avatar?: string;
}

function getSiteInfoByWorkspace(
  availableSites?: AvailableSite[],
  siteMapping?: Record<string, string>,
): Map<string, SiteInfo> {
  const result = new Map<string, SiteInfo>();

  if (availableSites && availableSites.length > 0) {
    for (const site of availableSites) {
      const siteId = getSiteIdFromSiteMappingKey(site.ari);
      result.set(site.url, { siteId, avatar: site.avatar || undefined });
    }
    return result;
  }

  // Fallback: siteMapping only (no avatar)
  for (const [siteMappingKey, workspace] of Object.entries(siteMapping ?? {})) {
    const siteId = getSiteIdFromSiteMappingKey(siteMappingKey);
    result.set(workspace, { siteId });
  }

  return result;
}

function getWorkspaceSiteId(
  workspace: string,
  siteIdsByWorkspace: ReadonlyMap<string, string>,
): string | null {
  const parsedWorkspace = parseWorkspace(workspace);
  return (
    siteIdsByWorkspace.get(workspace) ??
    siteIdsByWorkspace.get(parsedWorkspace.url) ??
    siteIdsByWorkspace.get(parsedWorkspace.name) ??
    null
  );
}

const toOption = (
  workspace: string,
  siteIdsByWorkspace: ReadonlyMap<string, string>,
  siteInfoByWorkspace?: ReadonlyMap<string, SiteInfo>,
): WorkspaceOption => {
  const parsedWorkspace = parseWorkspace(workspace);
  const siteInfo =
    siteInfoByWorkspace?.get(workspace) ??
    siteInfoByWorkspace?.get(parsedWorkspace.url) ??
    siteInfoByWorkspace?.get(parsedWorkspace.name);
  return {
    value: workspace,
    label: parsedWorkspace.name,
    siteId: siteInfo?.siteId ?? getWorkspaceSiteId(workspace, siteIdsByWorkspace),
    workspace,
    avatarUrl: siteInfo?.avatar,
  };
};

function groupWorkspaceOptionsByMostUsedSites({
  options,
  mostUsedSites,
  recentLabel,
  allSitesLabel,
}: {
  options: WorkspaceOption[];
  mostUsedSites: readonly RovoMostUsedSite[];
  recentLabel: string;
  allSitesLabel: string;
}): WorkspaceOptions {
  if (mostUsedSites.length === 0) {
    return options;
  }

  const optionsBySiteId = new Map<string, WorkspaceOption[]>();
  for (const option of options) {
    if (!option.siteId) {
      continue;
    }

    optionsBySiteId.set(option.siteId, [
      ...(optionsBySiteId.get(option.siteId) ?? []),
      option,
    ]);
  }

  // Keep duplicated recent/all rows as distinct option objects so focus/hover
  // styling only applies to the row the user is actually interacting with.
  const mostUsedOptions = mostUsedSites.flatMap((site) =>
    (optionsBySiteId.get(site.siteId) ?? []).map((option) => ({
      ...option,
      value: `recent:${option.workspace}`,
    })),
  );

  if (mostUsedOptions.length === 0) {
    return options;
  }

  return [
    { label: recentLabel, options: mostUsedOptions },
    {
      label: allSitesLabel,
      options,
    },
  ];
}

function isWorkspaceOptionGroup(
  optionOrGroup: WorkspaceOption | GroupType<WorkspaceOption>,
): optionOrGroup is GroupType<WorkspaceOption> {
  return Array.isArray((optionOrGroup as GroupType<WorkspaceOption>).options);
}

const Option = (
  props: OptionProps<WorkspaceOption, true> & {
    isWorkspaceSelected?: boolean;
  },
): ReactElement => {
  const workspace = props.data.workspace;
  const { name, url } = parseWorkspace(workspace);
  const isSelected = props.isWorkspaceSelected ?? props.isSelected;

  return (
    <components.Option {...props} isSelected={isSelected}>
      <Inline space="space.100" alignBlock="center">
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          tabIndex={-1}
          aria-hidden
        />
        <SiteTile avatarUrl={props.data.avatarUrl} />
        <Stack>
          <Text weight="medium">{name}</Text>
          {url !== name && (
            <Text size="small" color="color.text.subtlest">
              {url}
            </Text>
          )}
        </Stack>
      </Inline>
    </components.Option>
  );
};

const chipColor = (option: OptionType): SiteTileColor => {
  const avatarUrl = (option as Partial<WorkspaceOption>).avatarUrl;
  return getAvatarIconColor(avatarUrl) ?? ACCENT.teal;
};

const MultiValueLabel = (
  props: ComponentProps<typeof components.MultiValueLabel>,
): ReactElement => {
  const { name, url } = parseWorkspace(
    String(
      (props.data as Partial<WorkspaceOption>).workspace ??
        (props.data as OptionType).value,
    ),
  );
  return (
    <components.MultiValueLabel {...props}>
      {url !== name ? `${name} | ${url}` : name}
    </components.MultiValueLabel>
  );
};

const multiValueStyles: StylesConfig<WorkspaceOption, true> = {
  multiValue: (base, state) => {
    const color = chipColor(state.data);

    return {
      ...base,
      backgroundColor: color.background,
      borderColor: color.border,
      borderStyle: 'solid',
      borderWidth: token('border.width', '1px'),
    };
  },
  multiValueLabel: (base, state) => ({
    ...base,
    color: chipColor(state.data).foreground,
  }),
  multiValueRemove: (base, state) => ({
    ...base,
    color: chipColor(state.data).foreground,
  }),
};

const formatGroupLabel = (group: GroupType<WorkspaceOption>): ReactElement => (
  <Box xcss={groupLabelStyles}>
    <Text size="small" color="color.text.subtlest" weight="bold">
      {group.label}
    </Text>
  </Box>
);

function useWorkspaceOptions(
  availableWorkspaces: readonly string[],
  siteMapping?: Record<string, string>,
  availableSites?: AvailableSite[],
): WorkspaceOption[] {
  const siteIdsByWorkspace = useMemo(
    () => getSiteIdsByWorkspace(siteMapping),
    [siteMapping],
  );

  const siteInfoByWorkspace = useMemo(
    () => getSiteInfoByWorkspace(availableSites, siteMapping),
    [availableSites, siteMapping],
  );

  return useMemo(
    () =>
      availableWorkspaces.map((workspace) =>
        toOption(workspace, siteIdsByWorkspace, siteInfoByWorkspace),
      ),
    [availableWorkspaces, siteIdsByWorkspace, siteInfoByWorkspace],
  );
}

function useSelectedWorkspaceValues(selectedWorkspace: string | null) {
  return useMemo(
    () =>
      new Set(
        selectedWorkspace
          ?.split(',')
          .map((workspace) => workspace.trim())
          .filter(Boolean) ?? [],
      ),
    [selectedWorkspace],
  );
}

function getWorkspaceFromOption(option: OptionType): string {
  return String((option as Partial<WorkspaceOption>).workspace ?? option.value);
}

function serializeWorkspaceValues(workspaces: Iterable<string>) {
  const uniqueWorkspaces = Array.from(new Set(workspaces)).filter(Boolean);
  return uniqueWorkspaces.length > 0 ? uniqueWorkspaces.join(',') : null;
}

function serializeSelectedOptions(options: readonly OptionType[] | null) {
  return options && options.length > 0
    ? serializeWorkspaceValues(options.map(getWorkspaceFromOption))
    : null;
}

function serializeSelectionChange(
  options: readonly OptionType[] | null,
  actionMeta: { action: string; option?: OptionType | null },
  selectedWorkspaceValues: ReadonlySet<string>,
) {
  const option = actionMeta.option as WorkspaceOption | undefined;
  if (
    (actionMeta.action === 'select-option' ||
      actionMeta.action === 'deselect-option') &&
    option?.workspace &&
    selectedWorkspaceValues.has(option.workspace)
  ) {
    const nextSelectedWorkspaceValues = new Set(selectedWorkspaceValues);
    nextSelectedWorkspaceValues.delete(option.workspace);
    return serializeWorkspaceValues(nextSelectedWorkspaceValues);
  }

  return serializeSelectedOptions(options);
}

export const LegacySelector: FunctionComponent<SelectorProps> = ({
  availableWorkspaces,
  siteMapping,
  availableSites,
  selectedWorkspace,
  setSelectedWorkspace,
  mostUsedSites = [],
  shouldGroupMostUsedSites = false,
}) => {
  const { formatMessage } = useIntl();
  const workspaceOptions = useWorkspaceOptions(
    availableWorkspaces,
    siteMapping,
    availableSites,
  );
  const selectedWorkspaceValues = useSelectedWorkspaceValues(selectedWorkspace);
  const selectedOptions = workspaceOptions.filter((option) =>
    selectedWorkspaceValues.has(String(option.value)),
  );

  const groupedWorkspaceOptions = useMemo(
    () =>
      shouldGroupMostUsedSites
        ? groupWorkspaceOptionsByMostUsedSites({
            options: workspaceOptions,
            mostUsedSites,
            recentLabel: formatMessage(messages.recentGroupLabel),
            allSitesLabel: formatMessage(messages.allSitesGroupLabel),
          })
        : workspaceOptions,
    [formatMessage, mostUsedSites, shouldGroupMostUsedSites, workspaceOptions],
  );

  return (
    <Box xcss={siteSelectStyles}>
      <Select
        options={groupedWorkspaceOptions}
        isMulti
        isSearchable={true}
        placeholder={formatMessage(messages.sitePlaceholder)}
        value={selectedOptions}
        onChange={(options, actionMeta) => {
          setSelectedWorkspace(
            serializeSelectionChange(
              (options ?? []) as readonly OptionType[],
              actionMeta,
              selectedWorkspaceValues,
            ),
          );
        }}
      />
    </Box>
  );
};

const REFRESHED_INPUT_ID = 'oauth-consent-rovo-site-select';

export const RefreshedSelector: FunctionComponent<SelectorProps> = ({
  availableWorkspaces,
  siteMapping,
  availableSites,
  selectedWorkspace,
  setSelectedWorkspace,
  mostUsedSites = [],
  shouldGroupMostUsedSites = false,
}) => {
  const { formatMessage } = useIntl();
  const workspaceOptions = useWorkspaceOptions(
    availableWorkspaces,
    siteMapping,
    availableSites,
  );
  const selectedWorkspaceValues = useSelectedWorkspaceValues(selectedWorkspace);

  const allSorted = useMemo(
    () => [...workspaceOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [workspaceOptions],
  );

  const groupedOptions: GroupType<WorkspaceOption>[] = useMemo(() => {
    const groupedWorkspaceOptions =
      shouldGroupMostUsedSites && mostUsedSites.length > 0
        ? groupWorkspaceOptionsByMostUsedSites({
            options: allSorted,
            mostUsedSites,
            recentLabel: formatMessage(messages.recentGroupLabel),
            allSitesLabel: formatMessage(messages.allSitesGroupLabel),
          })
        : [
            {
              label: formatMessage(messages.allSitesGroupLabel),
              options: allSorted,
            },
          ];

    const ungroupedOptions: WorkspaceOption[] = [];
    const groups: GroupType<WorkspaceOption>[] = [];

    for (const optionOrGroup of groupedWorkspaceOptions) {
      if (isWorkspaceOptionGroup(optionOrGroup)) {
        groups.push(optionOrGroup);
      } else {
        ungroupedOptions.push(optionOrGroup);
      }
    }

    return ungroupedOptions.length > 0
      ? [
          ...groups,
          {
            label: formatMessage(messages.allSitesGroupLabel),
            options: ungroupedOptions,
          },
        ]
      : groups;
  }, [allSorted, formatMessage, mostUsedSites, shouldGroupMostUsedSites]);

  const selectedOptions = workspaceOptions.filter((option) =>
    selectedWorkspaceValues.has(String(option.value)),
  );
  const RefreshedOption = useMemo(
    () =>
      (props: OptionProps<WorkspaceOption, true>): ReactElement => (
        <Option
          {...props}
          isWorkspaceSelected={selectedWorkspaceValues.has(
            props.data.workspace,
          )}
        />
      ),
    [selectedWorkspaceValues],
  );

  return (
    <Box xcss={refreshedWrapperStyles}>
      <Stack space="space.050" xcss={refreshedStackStyles}>
        <Label htmlFor={REFRESHED_INPUT_ID}>
          {formatMessage(messages.siteLabelCount, {
            count: selectedOptions.length,
          })}
        </Label>
        <Select<WorkspaceOption, true>
          inputId={REFRESHED_INPUT_ID}
          options={groupedOptions}
          isMulti
          isSearchable={true}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          placeholder={formatMessage(messages.sitePlaceholder)}
          value={selectedOptions}
          components={{
            Option: RefreshedOption,
            MultiValueLabel:
              MultiValueLabel as typeof components.MultiValueLabel,
          }}
          formatGroupLabel={formatGroupLabel}
          styles={multiValueStyles}
          onChange={(options, actionMeta) => {
            setSelectedWorkspace(
              serializeSelectionChange(
                (options ?? []) as readonly OptionType[],
                actionMeta,
                selectedWorkspaceValues,
              ),
            );
          }}
        />
      </Stack>
    </Box>
  );
};

const SiteSelector: FunctionComponent = (): ReactElement => {
  const { selectedWorkspace, setSelectedWorkspace } = useSelectedWorkspace();
  const { availableWorkspaces, siteMapping, availableSites } = useInfo();
  const refreshed = useRovoVisualRefresh();
  const workspaces = availableWorkspaces ?? [];
  const shouldFetchMostUsedSites = workspaces.length > 3;
  const { mostUsedSites } = useRovoMostUsedSites(shouldFetchMostUsedSites);

  const props: SelectorProps = {
    availableWorkspaces: workspaces,
    siteMapping,
    availableSites,
    selectedWorkspace: selectedWorkspace ?? null,
    setSelectedWorkspace: (workspace) => setSelectedWorkspace?.(workspace),
    mostUsedSites,
    shouldGroupMostUsedSites: shouldFetchMostUsedSites,
  };

  return refreshed ? (
    <RefreshedSelector {...props} />
  ) : (
    <LegacySelector {...props} />
  );
};

export default SiteSelector;
